<template>
  <Layout>
    <div class="page-container">
      <div class="_mc-list-header">
        <PageHeader :title="title" subTitle="">
          <template #afterTitle>
            <div class="d-flex gap-2">
              <Back
                :hasChanged="hasChanged"
                :backToAddendumHistory="versionStatus == 'INACTIVE' && versionStatus != 'DRAFT'"
              />

              <ActivateAddendumButton
                v-if="show.activateAddendumBtn"
                :version="showResponse?.version"
              />
              <FormActions v-if="show.formActions" />
              <CreateAddendumButton v-if="show.createAddendumBtn" />
              <RenewContractButton v-if="show.renewContractBtn" />
              <McTransitionStatus
                v-if="show.transitionStatusBtn"
                :isCreating="isCreating"
                :entityData="showResponse"
                :statusTransitions="statusTransitions"
                :isDisabled="isLoading"
                @transition="handleTransitionStatus"
              />
            </div>
          </template>
        </PageHeader>
      </div>

      <McWizard v-if="show.wizard" :items="wizardItems" :activeKey="wizardActiveStatus" />

      <FormulateForm
        v-model="formValues"
        :errors="formErrors"
        class="contract-detail-form"
        name="contract-detail-form"
        @submit="handleSubmit"
      >
        <div class="__header-container">
          <h4 class="__header-text">Informasi Detail</h4>
          <McButton
            v-if="canEditFields"
            :disabled="!hasChanged"
            variant="secondary"
            :text="$t('global.cancel')"
            icon="icon-outline_close"
            @click="$refs['mc-modal-cancel'].show()"
          />

          <McButton
            v-if="canEditFields"
            :disabled="!hasChanged"
            variant="primary"
            text="Simpan"
            icon="icon-outline_save"
            @click="triggerSave"
          />
        </div>

        <b-card>
          <b-row>
            <b-col
              v-bind="field.colAttrs ?? { md: 3 }"
              v-for="field in contractDetailFields"
              :key="field.name"
            >
              <FormulateInput
                v-bind="field"
                :help="field?.validation?.includes('required')"
                validation-behavior="live"
                :disabled="field.disabled ?? isLoading"
                :initialObject="field.getInitialObject?.(showResponse)"
              >
                <template v-if="field.name == 'payment_term'" #suffix>
                  <span class="__payment-term-suffix">
                    {{ field.suffix }}
                  </span>
                </template>

                <template v-if="!canEditFields" #element="{ slotProps, model }">
                  <div v-if="slotProps?.component?.initialObject" class="fw-bold">
                    {{ slotProps.component.initialObject?.name }}
                  </div>
                  <div v-else-if="field.name == 'payment_term'" class="fw-bold">
                    {{ model }} {{ field.suffix }}
                  </div>
                  <div v-else-if="field.type == 'date-picker'" class="fw-bold">
                    {{ formatIndexDate(model, field.format).date }}
                  </div>
                  <div v-else class="fw-bold">
                    {{ model == '' ? '-' : model }}
                  </div>
                </template>

                <template #help v-if="!canEditFields"><span></span></template>
              </FormulateInput>
            </b-col>
          </b-row>
        </b-card>
      </FormulateForm>

      <Attachments
        :isCreatingContract="isCreating"
        :canEditFields="canEditFields"
        :vendorId="vendorId"
        :contractId="contractId"
        :versionId="versionId"
      />

      <Rates
        :canEditFields="canEditFields"
        :isCreatingContract="isCreating"
        :vendorId="vendorId"
        :contractId="contractId"
        :versionId="versionId"
      />
      <b-modal
        class="mc-modal"
        id="mc-modal-cancel"
        ref="mc-modal-cancel"
        title="Batal"
        size="md"
        centered
        scrollable
      >
        <div class="card-body p-2">
          <p>
            Apakah Anda yakin ingin membatalkan perubahan <strong>Informasi Kontrak Vendor</strong>?
          </p>
        </div>
        <template #modal-footer="{ cancel }">
          <b-row class="mc-modal__filter-footer mx-0">
            <b-col sm="12" md="12" class="mc-modal__filter-footer-btn">
              <McButton @click="cancel()" :text="'Tidak'" variant="secondary" class="me-2" />
              <McButton @click="handleCancel" :text="'Ya'" variant="primary" />
            </b-col>
          </b-row>
        </template>
      </b-modal>
    </div>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McWizard from '@/components/McWizard'
import McButton from '@/components/McButton'
import McTransitionStatus from '@/components/McTransitionStatus'

import { handleApiValidation } from '@/helpers/resourceful/rest-api-field-validation'
import { diffObject } from '@/helpers/resourceful/compare'
import { formatIndexDate } from '@/helpers/tzDate'
import { some, every } from 'lodash'

import * as Contract from '@/services/rest-api/lib/contract/contract'
import * as Vendor from '@/services/rest-api/lib/vendor/vendor'
import * as Transition from '@/services/rest-api/lib/contract/transition'

import Attachments from './components/Attachments'
import Rates from './components/Rates'
import Back from './components/Back'
import FormActions from './components/FormActions'
import CreateAddendumButton from './components/CreateAddendumButton'
import ActivateAddendumButton from './components/ActivateAddendumButton'
import RenewContractButton from './components/RenewContractButton'

import {
  contractDetailFields,
  contractDetailFieldsAddendum,
  contractDetailFieldsCreateFromVendorPage,
} from './lib/fields/contract-detail-fields'
import {
  transformContractStoreRequest,
  transformContractUpdateRequest,
  transformContractShowResponse,
} from './lib/helpers/transform-data'
import apiErrorMappings from './lib/constants/api-error-mappings'
import statusTransitions from './lib/constants/status-transitions'
import * as wizardItems from './lib/constants/wizard-items'

export default {
  page: {
    title: 'Master Vendor Contract Form',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McWizard,
    McButton,
    Attachments,
    Rates,
    Back,
    McTransitionStatus,
    FormActions,
    CreateAddendumButton,
    ActivateAddendumButton,
    RenewContractButton,
  },
  data() {
    return {
      formValues: {},
      formErrors: {},

      showResponse: {},

      isLoading: true,
    }
  },
  computed: {
    title() {
      if (this.isCreating) return 'Tambah Kontrak Vendor Baru'

      const number = this.showResponse?.number
      const addendum = this.showResponse?.version?.addendum

      if (addendum > 0) return `Detail Kontrak Vendor #${number} - Addendum ${addendum}`
      if (number) return `Detail Kontrak Vendor #${number}`

      return 'Detail Kontrak Vendor'
    },
    hasChanged() {
      if (this.isLoading) return false

      const currentFormValues = this.formValues
      let prevFormValues = null

      // If on Create Form, previous form values is empty/default value
      if (this.isCreating) prevFormValues = {}
      // Else, get previous form values from showResponse
      else prevFormValues = transformContractShowResponse(this.showResponse)

      const [_, hasChanged] = diffObject(prevFormValues, currentFormValues)

      return hasChanged
    },
    wizardItems() {
      if (this.contractStatus === 'Non Aktif') return wizardItems.wizardItemsInactive
      if (this.contractStatus === 'Aktif' && this.versionStatus === 'DRAFT')
        return wizardItems.wizardItemsAddendum

      return wizardItems.wizardItemsDefault
    },
    wizardActiveStatus() {
      if (this.versionStatus === 'DRAFT') return 'Draf'

      return this.contractStatus
    },
    prefixUrl() {
      return {
        vendorId: this.vendorId,
        contractId: this.contractId,
        versionId: this.versionId,
      }
    },
    isCreating() {
      const { vendorId, contractId, versionId } = this.$route.params
      return vendorId == null || contractId == null || versionId == null
    },
    vendorId() {
      return this.$route.params.vendorId
    },
    contractId() {
      return this.$route.params.contractId
    },
    versionId() {
      return this.$route.params.versionId
    },
    queryVendorId() {
      return this.$route.query.vendorId
    },
    contractStatus() {
      return this.showResponse?.status?.name ?? 'Draf'
    },
    versionStatus() {
      return this.showResponse?.version?.status ?? 'DRAFT'
    },
    canEditFields() {
      return this.contractStatus == 'Draf' || this.versionStatus == 'DRAFT'
    },
    statusTransitions() {
      return statusTransitions
    },
    contractDetailFields() {
      if (this.isCreating == false && this.versionStatus == 'DRAFT')
        return contractDetailFieldsAddendum

      if (this.isCreating == true && this.queryVendorId !== undefined)
        return contractDetailFieldsCreateFromVendorPage

      return contractDetailFields
    },
    show() {
      return {
        createAddendumBtn: this.contractStatus == 'Aktif' && this.versionStatus == 'ACTIVE',
        renewContractBtn: this.contractStatus == 'Kadaluwarsa' && this.versionStatus == 'ACTIVE',
        activateAddendumBtn: this.contractStatus == 'Aktif' && this.versionStatus == 'DRAFT',
        formActions: this.isCreating == false && this.versionStatus == 'ACTIVE',
        transitionStatusBtn: this.versionStatus == 'ACTIVE',
        wizard: this.versionStatus != 'INACTIVE',
      }
    },
  },
  watch: {
    '$route.params': 'showData',
    '$route.query': 'showData',
  },
  async mounted() {
    // If creating, but vendorId is provided (redirect from vendor form), get vendor detail
    if (this.isCreating) {
      const vendor = await this.getVendorDetail(this.queryVendorId)

      if (!vendor) {
        this.isLoading = false
        return
      }

      this.showResponse = { vendor }
      this.formValues = transformContractShowResponse(this.showResponse)
    } else {
      await this.showData()
    }

    this.isLoading = false
  },
  methods: {
    // Imported
    some,
    every,
    formatIndexDate,

    // Core
    async showData({ vendorId, contractId, versionId } = {}) {
      vendorId = vendorId ?? this.vendorId
      contractId = contractId ?? this.contractId
      versionId = versionId ?? this.versionId

      const res = await Contract.show(vendorId, contractId, versionId)

      if (res.errors) {
        this.$mcToast.error('Gagal mengambil data vendor')
        this.isLoading = false
        return
      }

      this.showResponse = res.data
      this.formValues = transformContractShowResponse(res.data)

      this.isLoading = false
    },
    async handleSubmit(data) {
      this.isLoading = true

      const vendorId = this.formValues?.vendor_id
      if (vendorId == null) {
        this.isLoading = false
        this.$mcToast.error('Terjadi kesalahan, silahkan coba lagi')
        return
      }

      let payload = null
      let res = null

      if (this.isCreating) {
        payload = transformContractStoreRequest(data)
        res = await Contract.store(vendorId, payload)
      } else {
        const oldData = transformContractShowResponse(this.showResponse)
        payload = transformContractUpdateRequest(data, oldData)

        res = await Contract.update(
          this.prefixUrl.vendorId,
          this.prefixUrl.contractId,
          this.prefixUrl.versionId,
          payload
        )
      }

      if (!res) {
        this.$mcToast.error('Gagal menyimpan data')
        this.isLoading = false
        return
      }

      if (res?.errors) {
        this.$mcToast.error('Terdapat kesalahan dalam input anda')
        this.formErrors = handleApiValidation(apiErrorMappings, res.errors)
        this.isLoading = false
        return
      } else {
        this.formErrors = {}
      }

      this.$mcToast.success('Data berhasil disimpan')

      let prefixUrl = this.prefixUrl

      if (this.isCreating) {
        prefixUrl = {
          vendorId: res.data.vendor_id,
          contractId: res.data.contract_id,
          versionId: res.data.version_id,
        }

        this.$router.push({
          name: 'Master Vendor Contract Detail',
          params: {
            vendorId: res.data.vendor_id,
            contractId: res.data.contract_id,
            versionId: res.data.version_id,
          },
          query: this.$route.query,
        })
      }

      await this.showData(prefixUrl)

      this.isLoading = false
    },

    async handleTransitionStatus(data) {
      if (this.hasChanged) {
        this.$mcToast.error('Simpan perubahan terlebih dahulu')
        return
      }

      const newStatus = data?.transition?.status?.id

      if (['ACTIVE', 'INACTIVE'].includes(newStatus)) {
        this.isLoading = true

        const res = await Transition.transition(this.prefixUrl, {
          status: newStatus,
        })

        if (!res) {
          this.$mcToast.error('Terjadi kesalahan')
          this.isLoading = false
          return
        }

        if (res.errors) {
          const {
            attachment: attachmentErrors,
            rates: ratesErrors,
            ...formErrors
          } = handleApiValidation(apiErrorMappings, res.errors)

          if (attachmentErrors) this.$mcToast.error(attachmentErrors)
          if (ratesErrors) this.$mcToast.error(ratesErrors)

          this.formErrors = formErrors
          this.isLoading = false
          return
        }

        await this.showData(this.prefixUrl)
        this.$mcToast.success('Data berhasil disimpan')
      }
    },

    // Utility
    triggerSave() {
      this.$formulate.submit('contract-detail-form')
    },
    handleCancel() {
      this.formValues = this.isCreating ? {} : transformContractShowResponse(this.showResponse)
      this.$refs['mc-modal-cancel'].hide()
    },
    async getVendorDetail(vendorId) {
      if (vendorId == null) return

      const res = await Vendor.show(vendorId)
      if (res.errors) return

      return res.data
    },
  },
}
</script>

<style lang="scss" scoped>
@import './styles/form.scss';
</style>
